import { useCallback, useMemo, useState } from 'react';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { gql } from '@apollo/client';

import { useAccessToken } from '@hooks/useAccessToken';
import { usePlayers } from '@hooks/usePlayers';
import { usePlayerStats } from '@hooks/usePlayerStats';
import { useRecentlyViewedPlayers } from '@hooks/useRecentlyViewedPlayers';
import { useLeagueContext } from '@hooks/useLeagueContext';
import { useTeams } from '@hooks/useTeams';
import { useIsAppView } from '@hooks/useIsAppView';
import { usePostHogFeatureFlag } from '@hooks/usePostHogFeatureFlag';
import { PostHogFeatureFlags } from '@lib/posthog-feature-flags';

import EndOfSeasonHoldingMessage from '@components/end-of-season-holding-message';
import Title from '@components/title';
import WideLayout from '@components/layout/wide';
import { getYoastHeadSEO } from '@lib/utils';
import { client, getPostSEOBySlug } from 'apollo-client';

import { PlayerProfileView } from '@components/player-profile/profile-view';
import {
  PlayerProfileLanding,
  IN_FORM_GAMEWEEK_COUNT,
  type ClubLink,
  type InFormPlayer,
  type LandingPosition
} from '@components/player-profile/landing';

import { readStat } from '@lib/page-utils/player-profile/metrics';
import {
  FIRST_GAMEWEEK,
  toGameweekRange
} from '@lib/page-utils/player-profile/filters';
import type { Player as FfhPlayer } from '@api-service/players';
import type { PlayerStatsPosition } from '@api-service/player-stats';

const FFHModal = dynamic(() => import('@components/modal'));
const Paywall = dynamic(() => import('@components/paywall'));

const IN_FORM_STAT_FIELDS = 'fpl';
const IN_FORM_CARD_COUNT = 4;
const MOST_OWNED_COUNT = 5;
/** Covers a full Premier League squad when browsing by club. */
const CLUB_SQUAD_COUNT = 30;

const PAYWALL_TITLE = 'Unlock the player profile tool 🔒';
const PLAYER_PROFILE_PATH = '/player-profile';

type PlayerProfilePageProps = {
  pageData: { seo?: { fullHead?: string } } | null;
};

/**
 * The search-led landing state. A URL naming a player
 * (`/player-profile?id=…`) 301s to its slug URL under /fpl/players before it
 * reaches the page, so the profile branch below only serves client-side
 * navigations that predate the redirect (an open tab mid-session).
 */
const PlayerProfilePage = ({ pageData }: PlayerProfilePageProps) => {
  const router = useRouter();
  const optaId = typeof router.query.id === 'string' ? router.query.id : null;
  const { isAppView } = useIsAppView();
  const isPlayerProfilePageClosed = usePostHogFeatureFlag(
    PostHogFeatureFlags.playerProfilePageIsClosed,
    false
  );

  const [isPaywallOpen, setIsPaywallOpen] = useState(false);
  const [inFormPosition, setInFormPosition] = useState<LandingPosition>('All');
  const [selectedClubId, setSelectedClubId] = useState<string | null>(null);

  const { accessToken } = useAccessToken();

  const openPaywall = useCallback(() => setIsPaywallOpen(true), []);

  const yoastHead = getYoastHeadSEO(
    pageData?.seo?.fullHead ?? '',
    '/player-profile'
  );

  const { context } = useLeagueContext();
  const currentSeason = context?.season ?? null;
  const isPreSeason = context?.seasonStatus === 'PRE_SEASON';
  const latestGameweek = isPreSeason
    ? null
    : context?.currentGameweek?.number ?? null;
  const defaultSeason =
    isPreSeason && currentSeason !== null ? currentSeason - 1 : currentSeason;

  const { teams } = useTeams();
  const { recentlyViewed } = useRecentlyViewedPlayers();

  /* ---------------------------------------------------------------------- */
  /* Landing state data                                                     */
  /* ---------------------------------------------------------------------- */

  const inFormRange = toGameweekRange(
    'last6',
    latestGameweek ?? FIRST_GAMEWEEK
  );
  const inFormStart = Math.max(
    FIRST_GAMEWEEK,
    inFormRange.maxGameweek - IN_FORM_GAMEWEEK_COUNT + 1
  );

  const { data: inFormData, isLoading: inFormIsLoading } = usePlayerStats({
    fields: IN_FORM_STAT_FIELDS,
    aggregation: 'total',
    venue: 'all',
    minGameweek: inFormStart,
    maxGameweek: inFormRange.maxGameweek,
    sortBy: 'fpl.points',
    sortDirection: 'desc',
    limit: IN_FORM_CARD_COUNT,
    // Pre-season there is no form to rank on, so the section does not render
    // and the leaderboard is not fetched at all.
    enabled: optaId == null && !isPreSeason,
    ...(inFormPosition === 'All'
      ? {}
      : { position: inFormPosition as PlayerStatsPosition }),
    ...(defaultSeason ? { season: defaultSeason } : {}),
    ...(accessToken ? { accessToken } : {})
  });

  const { data: mostOwnedData } = usePlayers({
    sortBy: 'ownership',
    sortDirection: 'desc',
    limit: MOST_OWNED_COUNT,
    exclude: 'predictions'
  });

  const inFormPlayers = useMemo<InFormPlayer[]>(
    () =>
      (inFormData?.pages.flatMap((page) => page.data) ?? []).map((row) => ({
        // The cross-season Opta UUID the profile route takes. Stats rows did
        // not carry it when this page was built, which left every in-form
        // card rendered as a permanently disabled button; the API exposes it
        // now (the FFH-2362 note asking for exactly this).
        optaId: row.optaUuid ?? null,
        displayName: row.displayName,
        position: row.position,
        teamShortName: row.team.shortName,
        price: row.price,
        points: readStat(row, 'fpl.points')
      })),
    [inFormData]
  );

  const mostOwned = useMemo<InFormPlayer[]>(
    () =>
      (mostOwnedData?.pages.flatMap((page) => page.data) ?? []).map(
        (ownedPlayer) => ({
          optaId: ownedPlayer.externalIds?.optaUuid ?? null,
          displayName: ownedPlayer.displayName,
          position: ownedPlayer.position,
          teamShortName: ownedPlayer.team.shortName,
          price: ownedPlayer.price,
          points: ownedPlayer.seasonStats?.points?.total ?? null
        })
      ),
    [mostOwnedData]
  );

  const clubs = useMemo<ClubLink[]>(
    () =>
      teams.map((team) => ({
        label: team.shortName,
        name: team.name,
        teamId: team.id
      })),
    [teams]
  );

  // Browsing by club lists that club's squad, most-owned first, so a chip
  // click visibly leads somewhere rather than silently filtering elsewhere.
  const { data: clubSquadData, isFetching: clubSquadIsFetching } = usePlayers({
    teamId: selectedClubId ?? undefined,
    sortBy: 'ownership',
    sortDirection: 'desc',
    limit: CLUB_SQUAD_COUNT,
    exclude: 'predictions',
    enabled: optaId == null && selectedClubId != null
  });

  const clubSquad = useMemo<InFormPlayer[]>(
    () =>
      (clubSquadData?.pages.flatMap((page) => page.data) ?? []).map(
        (squadPlayer) => ({
          optaId: squadPlayer.externalIds?.optaUuid ?? null,
          displayName: squadPlayer.displayName,
          position: squadPlayer.position,
          teamShortName: squadPlayer.team.shortName,
          price: squadPlayer.price,
          points: squadPlayer.seasonStats?.points?.total ?? null
        })
      ),
    [clubSquadData]
  );

  /* ---------------------------------------------------------------------- */
  /* Handlers                                                               */
  /* ---------------------------------------------------------------------- */

  const goToPlayer = useCallback(
    (nextOptaId: string) => {
      // Shallow, exactly as before the slug routes existed: the profile
      // branch below renders client-side with no server round trip. Shallow
      // navigation never triggers the next.config.js redirect, so browsing
      // from the landing stays instant; only a full load of the resulting
      // URL 301s to the canonical slug page. It is also what keeps this
      // flow testable in Cypress, where server-rendered player routes
      // cannot reach the mocked backend.
      router.push(
        { pathname: PLAYER_PROFILE_PATH, query: { id: nextOptaId } },
        undefined,
        { shallow: true }
      );
    },
    [router]
  );

  const handlePlayerClick = useCallback(
    (selectedPlayer: FfhPlayer) => {
      const nextOptaId =
        selectedPlayer.externalIds?.optaUuid ?? selectedPlayer.optaId;
      if (nextOptaId) goToPlayer(nextOptaId);
    },
    [goToPlayer]
  );

  const handleClubClick = useCallback(
    (clickedTeamId: string) =>
      setSelectedClubId((current) =>
        current === clickedTeamId ? null : clickedTeamId
      ),
    []
  );

  /* ---------------------------------------------------------------------- */
  /* Render                                                                 */
  /* ---------------------------------------------------------------------- */

  if (isPlayerProfilePageClosed) {
    return (
      <>
        <Head>{yoastHead}</Head>
        <WideLayout>
          <div className="bg-gray-50 px-2 pt-2 pb-10 sm:px-4 lg:pt-8">
            <EndOfSeasonHoldingMessage
              pageTitle="The Player Profile page"
              isAppView={isAppView}
            />
          </div>
        </WideLayout>
      </>
    );
  }

  if (optaId != null) {
    return (
      <>
        <Head>{yoastHead}</Head>
        <PlayerProfileView optaId={optaId} />
      </>
    );
  }

  return (
    <>
      <Head>{yoastHead}</Head>
      <Title>Player profiles</Title>
      <WideLayout content={false}>
        <FFHModal
          open={isPaywallOpen}
          handleClose={() => setIsPaywallOpen(false)}
        >
          <Paywall variant="modal" title={PAYWALL_TITLE} />
        </FFHModal>
        <PlayerProfileLanding
          onPlayerClick={handlePlayerClick}
          onPlayerSelect={goToPlayer}
          onPaywallOpen={openPaywall}
          mostOwned={mostOwned}
          shouldShowInForm={!isPreSeason}
          inFormPlayers={inFormPlayers}
          inFormPosition={inFormPosition}
          onInFormPositionChange={setInFormPosition}
          isInFormLoading={inFormIsLoading}
          recentlyViewed={recentlyViewed}
          clubs={clubs}
          selectedClubId={selectedClubId}
          clubSquad={clubSquad}
          isClubSquadLoading={selectedClubId != null && clubSquadIsFetching}
          onClubClick={handleClubClick}
        />
      </WideLayout>
    </>
  );
};

const SEO_REVALIDATE_SECONDS = 3000;

export const getStaticProps = async () => {
  const { data } = await client.query({
    query: gql`
      ${getPostSEOBySlug('fpl-player-profiles-fantasy-football-hub')}
    `
  });

  return {
    props: { pageData: data?.postBy ?? null },
    revalidate: SEO_REVALIDATE_SECONDS
  };
};

export default PlayerProfilePage;
